import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Package } from 'lucide-react';
import { api } from '../api/client';
import './TrackOrderPage.css';

/**
 * Page de retour de la passerelle de paiement (Konnect, Paymee…).
 * Le statut affiché est vérifié auprès du fournisseur côté serveur —
 * le paramètre `result` de l'URL n'est pas fiable.
 */
export default function PaymentReturnPage() {
  const [params] = useSearchParams();
  const [state, setState] = useState('checking'); // checking | paid | failed | pending | unknown
  const [orderCode, setOrderCode] = useState('');
  const ref = params.get('ref') || '';
  const result = params.get('result') || '';
  const tries = useRef(0);

  useEffect(() => {
    if (!ref) {
      setState('unknown');
      return;
    }
    let alive = true;
    let timer = null;
    const check = async () => {
      try {
        const res = await api(`/payments/verify/${encodeURIComponent(ref)}`);
        if (!alive) return;
        setOrderCode(res.order || '');
        if (res.status === 'paid') setState('paid');
        else if (res.status === 'failed') setState('failed');
        else {
          tries.current += 1;
          // La passerelle peut mettre quelques secondes à confirmer.
          if (tries.current < 8) timer = setTimeout(check, 2000);
          else setState('pending');
        }
      } catch {
        if (alive) setState(result === 'success' ? 'paid' : 'failed');
      }
    };
    check();
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  return (
    <div className="track-page">
      <div className="container">
        <div className="track-card">
          <div className="track-icon">
            <Package size={34} />
          </div>
          <h1>Paiement</h1>

          {state === 'checking' && (
            <p className="track-lead"><Loader size={16} style={{ verticalAlign: '-3px' }} /> Vérification du paiement en cours…</p>
          )}

          {state === 'paid' && (
            <div className="track-result" style={{ textAlign: 'center' }}>
              <CheckCircle size={44} style={{ color: '#16a34a' }} />
              <h2 style={{ margin: '10px 0 6px' }}>Paiement confirmé</h2>
              <p className="track-lead">
                {orderCode ? `Votre commande ${orderCode} est réglée. Vous recevrez une confirmation par email.` : 'Votre commande est réglée.'}
              </p>
              <div className="track-actions" style={{ justifyContent: 'center' }}>
                <Link to="/app/orders" className="btn btn-primary">Mes commandes</Link>
              </div>
            </div>
          )}

          {(state === 'failed' || state === 'unknown') && (
            <div className="track-result" style={{ textAlign: 'center' }}>
              <XCircle size={44} style={{ color: '#dc2626' }} />
              <h2 style={{ margin: '10px 0 6px' }}>
                {state === 'failed' ? 'Paiement refusé ou annulé' : 'Paiement introuvable'}
              </h2>
              <p className="track-lead">
                Aucun montant n&apos;a été débité. Vous pouvez réessayer depuis le suivi de votre commande.
              </p>
              <div className="track-actions" style={{ justifyContent: 'center' }}>
                <Link to="/suivi" className="btn btn-primary">Suivre ma commande</Link>
              </div>
            </div>
          )}

          {state === 'pending' && (
            <div className="track-result" style={{ textAlign: 'center' }}>
              <Loader size={44} style={{ color: '#d97706' }} />
              <h2 style={{ margin: '10px 0 6px' }}>Paiement en attente</h2>
              <p className="track-lead">
                La confirmation de votre banque n&apos;est pas encore arrivée. Le statut se mettra à jour
                automatiquement — consultez le suivi de commande dans quelques minutes.
              </p>
              <div className="track-actions" style={{ justifyContent: 'center' }}>
                <Link to="/suivi" className="btn btn-outline">Suivre ma commande</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
